import React, { useEffect, useMemo, useRef, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { tokens } from "../../theme";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  getUserError,
  getUserStatus,
  updateStatus,
  selectAllUsers,
  getUserSuccess,
  getByRestaurantUsers,
  deleteUser,
  blockUser,
  assignUser,
  unassignUser,
  selectUnAssignedUsers,
  fetchUnassignUser,
} from "../../features/userSlice";
import Loading from "../../components/loading";
import Error from "../../components/Error";
import AlertDialog from "../../components/alertDialog";
import { toast } from "react-toastify";
import TableErrorMessage from "../../components/noDataTable";
import {
  BlockList,
  getUserRoleLabel,
  UserRole,
  UserRoleList,
} from "../../models/user.model";
import { useRestaurant } from "../../hooks/RestaurantContext";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { fetchAuthUser, selectAuthUser } from "../../features/authSlice";
import FiltersDialog, {
  FiltersButton,
} from "../../components/filters/filtersDialog";
import FilterChipGroup from "../../components/filters/filterChipGroup";
import { NameSortList, applySort } from "../../models/sort.model";
import AppBarSearch from "../../global/appBarSearch";
import ParagraphHighliter from "../../components/paragraphHighliter";
import { ResponsiveProvider } from "../../hooks/uiHook";
import { useTranslation } from "react-i18next";
import TableActions from "../../components/tableActions";
const User = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const user = useSelector(selectAuthUser);
  const { restaurantId } = useRestaurant();
  const [localUsers, setLocalUsers] = useState([]);
  const [userStateFilter, setUserStateFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("");
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  // Draft copies bound to the FiltersDialog controls; committed to the
  // applied state above only when Appliquer is clicked.
  const [draftUserStateFilter, setDraftUserStateFilter] = useState("");
  const [draftRoleFilter, setDraftRoleFilter] = useState("");
  const [draftSortFilter, setDraftSortFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openAssingUser, setOpenAssingUser] = useState(false);
  const [openUnAssignUser, setOpenUnAssignUser] = useState(false);
  const [openBlock, setOpenBlock] = useState(false);
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const status = useSelector(getUserStatus);
  const error = useSelector(getUserError);
  const users = useSelector(selectAllUsers);
  const unAssignedUsers = useSelector(selectUnAssignedUsers);
  const success = useSelector(getUserSuccess);

  const hasAccess = [UserRole.admin].includes(user?.role);

  const handleClickOpen = (cardId) => {
    setOpen(true);
    setCardId(cardId);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("add");
  };

  const handleModify = (data) => {
    navigate("modify", { state: { user: data } });
  };

  const handleCloseAssingUser = () => {
    setOpenAssingUser(false);
    setSelectedUser("");
  };

  const handleUserAssignSubmit = async () => {
    if (!selectedUser) {
      toast.error("Veuillez sélectionner un utilisateur");
      return;
    }
    const assignedUser = unAssignedUsers.find((item) => item._id === selectedUser);
    if (!assignedUser) {
      toast.error("Veuillez sélectionner un utilisateur");
      return;
    }
    dispatch(
      assignUser({
        userId: selectedUser,
        role: assignedUser.role,
        restaurantId,
      })
    );
    handleCloseAssingUser();
  };

  useEffect(() => {
    if (status === "fetchData") setLocalUsers(users);
  }, [users, status]);

  useEffect(() => {
    if (status === "assignSuccess") {
      toast.success(success);
      dispatch(getByRestaurantUsers(restaurantId));
      dispatch(updateStatus());
    } else if (status === "assignError") {
      toast.error(error);
    }
  }, [status, error, dispatch, navigate, success, restaurantId]);

  useEffect(() => {
    if (status === "unassignSuccess") {
      toast.success(success);
      dispatch(getByRestaurantUsers(restaurantId));
      dispatch(updateStatus());
    } else if (status === "unassignError") {
      toast.error(error);
    }
  }, [status, error, dispatch, navigate, success, restaurantId]);

  const hasFetchedInMountRef = useRef(false);
  const lastFetchedRestaurantRef = useRef(restaurantId);
  useEffect(() => {
    const restaurantChanged =
      hasFetchedInMountRef.current &&
      lastFetchedRestaurantRef.current !== restaurantId;
    const alreadyLoaded = users.length > 0 && status === "fetchData";
    if (restaurantChanged || !alreadyLoaded) {
      dispatch(getByRestaurantUsers(restaurantId));
    }
    hasFetchedInMountRef.current = true;
    lastFetchedRestaurantRef.current = restaurantId;
    if (!user) dispatch(fetchAuthUser());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId, user]);

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true);
    dispatch(fetchUnassignUser(restaurantId));
  };

  const filteredUsersMemo = useMemo(() => {
    let filtered =
      localUsers?.filter((user) => user.role !== UserRole.admin) || [];

    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(lowerSearch) ||
          user.email?.toLowerCase().includes(lowerSearch)
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    if (userStateFilter !== "") {
      filtered = filtered.filter(
        (user) => String(user.isBlocked) === String(userStateFilter)
      );
    }

    return applySort(filtered, sortFilter, {
      getName: (user) => user.fullName,
    });
  }, [localUsers, search, roleFilter, userStateFilter, sortFilter]);

  // Live draft-vs-applied diff: Appliquer stays disabled until at least one
  // draft filter differs from what's currently applied, and re-disables
  // itself automatically if the user changes a filter back to match.
  const hasPendingChanges =
    draftRoleFilter !== roleFilter ||
    draftUserStateFilter !== userStateFilter ||
    draftSortFilter !== sortFilter;

  useEffect(() => {
    if (status === "blockSuccess") {
      toast.success(success);
      setLocalUsers((prev) =>
        prev.map((item) =>
          item._id === cardId ? { ...item, isBlocked: !item.isBlocked } : item
        )
      );
      dispatch(updateStatus());
    } else if (status === "blockError") {
      toast.error(error);
    }
  }, [status, success, error, dispatch, restaurantId, cardId]);

  useEffect(() => {
    if (status === "deleteSuccess") {
      toast.success(success);
      setLocalUsers((prev) => prev.filter((item) => item._id !== cardId));
      dispatch(updateStatus());
    } else if (status === "deleteError") {
      toast.error(error);
    }
  }, [status, error, success, dispatch, restaurantId, cardId]);

  return (
    <div className="main-application">
      <CssBaseline />
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          disableMobileDrawer
          searchText={t("search.byNameOrEmail")}
          handleSearch={(e) => setSearch(e.target.value)}
          title={t("pages.myUsers")}
          children={
            <>
              <FiltersButton
                onClick={() => {
                  setDraftRoleFilter(roleFilter);
                  setDraftUserStateFilter(userStateFilter);
                  setDraftSortFilter(sortFilter);
                  setFiltersDialogOpen(true);
                }}
                activeCount={
                  (roleFilter ? 1 : 0) +
                  (userStateFilter !== "" ? 1 : 0) +
                  (sortFilter ? 1 : 0)
                }
              />
            </>
          }
          actions={
            <>
              {hasAccess && (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAssingUser(true)}
                >
                  {t("actions.assignUser")}
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={handleSubmit}
              >
                {t("actions.addUser")}
              </Button>
            </>
          }
        />
      </ResponsiveProvider>
      <FiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
        onReset={() => {
          setDraftRoleFilter("");
          setRoleFilter("");
          setDraftUserStateFilter("");
          setUserStateFilter("");
          setDraftSortFilter("");
          setSortFilter("");
          setFiltersDialogOpen(false);
        }}
        onApply={() => {
          setRoleFilter(draftRoleFilter);
          setUserStateFilter(draftUserStateFilter);
          setSortFilter(draftSortFilter);
          setFiltersDialogOpen(false);
        }}
        disableApply={!hasPendingChanges}
      >
        <FilterChipGroup
          label={t("filters.allRoles")}
          options={UserRoleList}
          value={draftRoleFilter}
          onChange={setDraftRoleFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.allStates")}
          options={BlockList}
          value={draftUserStateFilter}
          onChange={setDraftUserStateFilter}
          allowAll
        />
        <FilterChipGroup
          label={t("filters.sortBy")}
          options={NameSortList}
          value={draftSortFilter}
          onChange={setDraftSortFilter}
        />
      </FiltersDialog>
      <main>
        <Box sx={{ my: 2 }}>
          <TableContainer>
            <Table
              sx={{
                backgroundColor: colors.primary[400],
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: colors.primary[700],
                }}
              >
                <TableRow>
                  <TableCell>{t("table.fullName")}</TableCell>
                  <TableCell>{t("table.email")}</TableCell>
                  <TableCell>{t("table.role")}</TableCell>
                  <TableCell>{t("table.status")}</TableCell>
                  <TableCell align="right">{t("table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status === "loadingFetchData" ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Loading size={8} />
                    </TableCell>
                  </TableRow>
                ) : status === "fetchError" ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Error>{error}</Error>
                    </TableCell>
                  </TableRow>
                ) : filteredUsersMemo && filteredUsersMemo.length > 0 ? (
                  filteredUsersMemo.map((card) => (
                    <TableRow key={card._id}>
                      <TableCell>
                        <ParagraphHighliter
                          title={card?.fullName}
                          search={search}
                        />
                      </TableCell>
                      <TableCell>
                        <ParagraphHighliter
                          title={card?.email}
                          search={search}
                        />
                      </TableCell>
                      <TableCell>{getUserRoleLabel(card.role)}</TableCell>
                      <TableCell>
                        <Chip
                          variant="outlined"
                          label={card.isBlocked ? t("common.blocked") : t("common.unblocked")}
                          color={card.isBlocked ? "error" : "success"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TableActions
                          actions={[
                            user?.role === UserRole.admin && {
                              type: "delete",
                              color: "error",
                              label: t("common.delete"),
                              onClick: () => handleClickOpen(card._id),
                            },
                            user &&
                              (user.role === UserRole.admin ||
                                user._id === card._id ||
                                (user.role === UserRole.manager &&
                                  card.role === UserRole.waiter)) && {
                                type: "edit",
                                color: "warning",
                                label: t("common.edit"),
                                onClick: () => handleModify(card),
                              },
                            user?.role === UserRole.admin && {
                              type: card.isBlocked ? "unblock" : "block",
                              color: "primary",
                              label: card.isBlocked
                                ? t("actions.unblockUser")
                                : t("actions.blockUser"),
                              onClick: () => {
                                setIsBlocked(!card.isBlocked);
                                setOpenBlock(true);
                                setCardId(card._id);
                                setSelectedUserName(card.fullName);
                              },
                            },
                            ((user?.role === UserRole.admin) ||
                              (user?.role === UserRole.manager &&
                                card.role === UserRole.waiter)) && {
                              type: "remove",
                              color: "secondary",
                              label: t("actions.remove"),
                              onClick: () => {
                                setOpenUnAssignUser(true);
                                setCardId(card._id);
                                setSelectedUserName(card.fullName);
                              },
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableErrorMessage cols={5} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </main>
      <AlertDialog
        handleClose={() => setOpen(false)}
        open={open}
        message="Voulez-vous supprimer cet utilisateur ?"
        cardId={cardId}
        deleteData={() =>
          dispatch(deleteUser({ userId: cardId, restaurantId }))
        }
      />
      <Dialog
        open={openAssingUser}
        onClose={handleCloseAssingUser}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              {t("actions.assignUser")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("actions.assignUserHint")}
            </Typography>
          </Box>
          <IconButton
            aria-label={t("common.cancel")}
            onClick={handleCloseAssingUser}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="assign-user-label">
              {t("fields.selectUser")}
            </InputLabel>
            <Select
              labelId="assign-user-label"
              value={selectedUser}
              onChange={(e) => {
                setSelectedUser(e.target.value);
              }}
              onOpen={handleDropdownOpen}
              onClose={() => setIsDropdownOpen(false)}
              label={t("fields.selectUser")}
              disabled={status === "fetchAssigning"}
              renderValue={(id) => {
                const picked = unAssignedUsers.find((item) => item._id === id);
                return picked ? picked.fullName : "";
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {unAssignedUsers.length === 0 ? (
                <MenuItem disabled>
                  {status === "fetchAssigning" || !isDropdownOpen
                    ? t("common.loading")
                    : status === "fetchAssignError"
                    ? error
                    : t("common.noUsersAvailable")}
                </MenuItem>
              ) : (
                unAssignedUsers.map((account) => (
                  <MenuItem key={account._id} value={account._id} sx={{ py: 1 }}>
                    <ListItemText
                      primary={account.fullName}
                      secondary={`${account.email} · ${getUserRoleLabel(account.role)}`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseAssingUser}
            sx={{ flex: 1 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUserAssignSubmit}
            disabled={!selectedUser || status === "assigning"}
            sx={{ flex: 1 }}
          >
            {status === "assigning" ? t("common.loading") : t("actions.assign")}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertDialog
        handleClose={() => setOpenUnAssignUser(false)}
        open={openUnAssignUser}
        title={t("actions.remove")}
        message={`Voulez-vous retirer l'utilisateur ${selectedUserName} ?`}
        cardId={cardId}
        deleteData={() =>
          dispatch(unassignUser({ userId: cardId, restaurantId }))
        }
      />
      <AlertDialog
        handleClose={() => setOpenBlock(false)}
        open={openBlock}
        title={isBlocked ? t("actions.blockUser") : t("actions.unblockUser")}
        message={`Voulez-vous ${
          isBlocked ? "bloquer" : "débloquer"
        } l'utilisateur ${selectedUserName} ?`}
        cardId={cardId}
        deleteData={() => {
          dispatch(
            blockUser({
              userId: cardId,
              restaurantId,
            })
          );
          setIsBlocked(false);
        }}
      />
    </div>
  );
};

export default User;
